import React, { useEffect, useRef, useState } from 'react';
import s from './PromptBlock.module.css';
import pdf from '../../../../assets/img/pdf.svg';
import docx from '../../../../assets/img/doc.svg';
import dropbox from '../../../../assets/img/dropbox.svg';
import drive from '../../../../assets/img/drive.svg';
import Button from '../../../../shared/ui/button/Button';
import { importDocument, uploadDocument } from '../../../../shared/api/textProjectApi';
import { useAppDispatch } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { selectCurrentProject } from '../../../../store/selectors/projectSelectors';
import { selectUser } from '../../../../store/selectors/authSelectors';
import { saveGoogleToken } from '../../../../shared/api/authApi';
import { toast } from 'react-toastify';
const UploadBlock = () => {
    const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [highlighted, setHighlighted] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
    const dispatch = useAppDispatch();
    const currentProject = useSelector(selectCurrentProject);
    const user = useSelector(selectUser);

    const tokenClient = useRef<any>(null);
    const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null);

    const handleOptionClick = (acceptType: string) => {
        if (!fileInputRef.current) return;

        fileInputRef.current.accept = acceptType;
        fileInputRef.current.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadedFile(file);
            setUploadedFileName(file.name);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setHighlighted(true);
    };

    const handleDragLeave = () => {
        setHighlighted(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setHighlighted(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            setUploadedFile(file);
            setUploadedFileName(file.name);
        }
    };

    const handleConfirm = async () => {
        try {
            if (uploadedFile && currentProject) {
                await dispatch(
                    uploadDocument({ file: uploadedFile, projectId: currentProject.id }),
                );
                console.log('uploadedFile', uploadedFile);
                toast.success('File uploaded successfully');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to upload file');
        }
    };

    const handleDropboxImport = async () => {
        if (!currentProject) return;

        try {
            // @ts-ignore
            window.Dropbox.choose({
                linkType: 'direct',
                multiselect: false,
                extensions: ['.pdf', '.docx'],
                success: async (files: any[]) => {
                    const link = files[0].link;
                    await dispatch(importDocument({ projectId: currentProject.id, link }));
                    toast.success('Dropbox file imported successfully');
                },
                cancel: () => console.log('Chooser closed'),
            });
        } catch (error: any) {
            toast.error(error?.message || 'Failed to import Dropbox file');
        }
    };
    useEffect(() => {
        if (!tokenClient.current) {
            // @ts-ignore
            tokenClient.current = google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: 'https://www.googleapis.com/auth/drive.readonly',
                callback: (tokenResponse: any) => {
                    dispatch(saveGoogleToken({ token: tokenResponse.access_token }));
                    setGoogleAccessToken(tokenResponse.access_token);
                    loadPicker(tokenResponse.access_token);
                },
            });
        }
    }, []);

    const handleGoogleDriveImport = () => {
        if (googleAccessToken) {
            loadPicker(googleAccessToken);
        } else {
            tokenClient.current.requestAccessToken({
                prompt: 'select_account',
                login_hint: user?.email,
            });
        }
    };

    const loadPicker = (accessToken: string) => {
        gapi.load('picker', () => {
            const picker = new google.picker.PickerBuilder()
                .addView(google.picker.ViewId.DOCS)
                .setOAuthToken(accessToken)
                .setDeveloperKey(API_KEY)
                .setCallback(pickerCallback)
                .build();
            picker.setVisible(true);
        });
    };

    const pickerCallback = async (data: any) => {
        try {
            if (data.action === google.picker.Action.PICKED) {
                const file = data.docs[0];
                const link = file.url;
                console.log('Selected file from Drive:', link);
                if (currentProject) {
                    await dispatch(
                        importDocument({
                            projectId: currentProject.id,
                            link,
                            accessToken: googleAccessToken ?? undefined,
                        }),
                    );
                    toast.success('Google Drive file imported successfully');
                }
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to import Google Drive file');
        }
    };

    return (
        <div className={s.container}>
            <div className={s.block_container}>
                <h3 className={s.bold_header}>Upload your own content</h3>

                <div className={s.options}>
                    <div className={s.option} onClick={() => handleOptionClick('.pdf')}>
                        <img alt="pdf" src={pdf} />
                        pdf
                    </div>
                    <div className={s.option} onClick={() => handleOptionClick('.docx')}>
                        <img alt="docx" src={docx} />
                        docx
                    </div>
                    <div className={s.option} onClick={handleDropboxImport}>
                        <img alt="dropbox" src={dropbox} />
                        Dropbox
                    </div>
                    <div className={s.option} onClick={handleGoogleDriveImport}>
                        <img alt="drive" src={drive} />
                        Drive
                    </div>
                </div>

                <input
                    type="file"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                />

                <h3 className={s.bold_header}>Attachment:</h3>
                <div className={s.prompt}>{uploadedFileName || 'No attachment'}</div>

                <p
                    className={`${s.drag_and_drop_container} ${highlighted ? s.highlight : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    Drag and drop your content here or upload it manually
                </p>
                {uploadedFileName && (
                    <Button
                        text="Confirm"
                        style={{
                            backgroundColor: '#FFDD35',
                        }}
                        onClick={handleConfirm}
                    />
                )}
            </div>
        </div>
    );
};

export default UploadBlock;
